import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import Select from 'react-select'
import { useErrorService, useUserService } from '@abroad/components'
import { ClearFilters } from '../common'
import { returnSelectionFieldStyle } from '../../constants/common'
import API from '../../utils/API'

const SchedulingTabFilters = ({
  selectedClientId,
  onClientChange,
  onFilterRender,
  isLoading,
  disabled = false,
  skipClientFetch = false,
}) => {
  const { user } = useUserService()
  const Error = useErrorService()
  const coachId = user?._id
  const [clients, setClients] = useState({
    isLoading: true,
    clients: [],
  })

  useEffect(() => {
    const fetchClients = async () => {
      if (skipClientFetch) {
        setClients({ isLoading: false, clients: [] })
        return
      }
      if (!coachId) return

      try {
        setClients({ isLoading: true, clients: [] })
        const { data } = await API.AdminAPI.scheduling.getCoachClients(
          coachId,
          { hasSow: true },
        )
        if (data?.success && data?.data) {
          setClients({ isLoading: false, clients: data.data })
        } else {
          setClients({ isLoading: false, clients: [] })
        }
      } catch (error) {
        Error.showError(error)
        setClients({ isLoading: false, clients: [] })
      }
    }

    fetchClients()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachId])

  const handleClientChange = useCallback(
    (selectedOption) => {
      onClientChange(selectedOption?.value || '')
    },
    [onClientChange],
  )

  const clientOptions = useMemo(
    () =>
      clients.clients?.map((client) => ({
        label: client?.fullName || client?.email || '-',
        value: client?._id,
      })) || [],
    [clients.clients],
  )

  const selectedClientOption = useMemo(
    () =>
      selectedClientId
        ? clientOptions.find((option) => option.value === selectedClientId)
        : null,
    [selectedClientId, clientOptions],
  )

  const clientSelectStyles = useMemo(
    () => ({
      ...returnSelectionFieldStyle(clients.isLoading, {}, false),
      menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
    }),
    [clients.isLoading],
  )

  const handleClearFilters = useCallback(() => {
    onClientChange('')
  }, [onClientChange])

  const filterUI = useMemo(
    () => ({
      clearFilter: selectedClientId ? (
        <ClearFilters
          wrapperClasses='mb-0'
          onClick={handleClearFilters}
          disabled={isLoading || disabled}
        />
      ) : null,
      inputField: (
        <div
          style={{
            minWidth: '250px',
            marginRight: '10px',
            opacity: disabled ? 0 : 1,
          }}>
          <Select
            value={selectedClientOption}
            onChange={handleClientChange}
            options={clientOptions}
            placeholder={
              clients.isLoading ? 'Fetching Clients...' : 'Select Client Name'
            }
            isSearchable={true}
            isClearable={true}
            isDisabled={clients?.isLoading || disabled}
            styles={clientSelectStyles}
            classNamePrefix='selection-input'
            menuPortalTarget={document.body}
            menuPosition='fixed'
            filterOption={(option, searchText) => {
              const label = option.label || ''
              return label.toLowerCase().includes(searchText.toLowerCase())
            }}
          />
        </div>
      ),
    }),
    [
      selectedClientOption,
      clientOptions,
      clients.isLoading,
      selectedClientId,
      isLoading,
      handleClientChange,
      handleClearFilters,
      clientSelectStyles,
      disabled,
    ],
  )

  const prevFilterUI = useRef(null)
  useEffect(() => {
    if (onFilterRender && filterUI !== prevFilterUI.current) {
      prevFilterUI.current = filterUI
      onFilterRender(filterUI)
    }
    return () => {
      if (onFilterRender) {
        onFilterRender(null)
      }
    }
  }, [filterUI, onFilterRender])

  return null
}

export default SchedulingTabFilters
